import FilterPill from './FilterPill.tsx';
import ViewSwitcher from './ViewSwitcher.tsx';
import TileSizeControl from '../../components/TileSizeControl.tsx';
import { Globe, Wand, Folder, Clock, ArrowUpDown, Users } from '../../lib/icons.tsx';
import type { FilterBarModel } from './filterOptions.ts';
import type { DeckSort, DeckStatusFilter, DeckViewMode, UpdatedWindow } from './deckFilterState.ts';
import type { TileSize } from '../../components/TileSizeControl.tsx';

/**
 * The dashboard's filter row: the four axes that narrow the grid, then sort
 * and the view switcher pushed to the far end.
 *
 * It replaced the `.view-nav` tab row (2026-09-02). The tab row could only
 * express ONE axis — status — and read as "you are looking at one of these",
 * which stopped being true the moment folder, model and updated-window joined
 * it. A row of pills says "each of these narrows what you see", which is what
 * these now do, and it leaves room for an axis to be added without redesigning
 * the row.
 *
 * Sort and the view switcher sit after a spacer because they are not filters:
 * nothing they do changes WHICH decks are shown, only their order and their
 * shape. Keeping them off the left cluster is what makes the chip row below
 * legible, since the chips speak for the left cluster alone.
 */
type FilterBarProps = {
  model: FilterBarModel;
  onStatus: (v: DeckStatusFilter) => void;
  onFolder: (v: string | null) => void;
  onModel: (v: string | null) => void;
  onOwner: (v: string | null) => void;
  onUpdated: (v: UpdatedWindow) => void;
  onSort: (v: DeckSort) => void;
  view: DeckViewMode;
  onView: (v: DeckViewMode) => void;
  /** Nested inside the grid view: table and list have no tile size, so the
   *  control is absent rather than disabled in those two. */
  tileSize: TileSize;
  onTileSize: (v: TileSize) => void;
};

export default function FilterBar({
  model,
  onStatus,
  onFolder,
  onModel,
  onOwner,
  onUpdated,
  onSort,
  view,
  onView,
  tileSize,
  onTileSize,
}: FilterBarProps) {
  return (
    <div className="filter-bar" role="group" aria-label="Filter and sort decks">
      <FilterPill<DeckStatusFilter>
        label={model.status.label}
        value={model.status.value}
        valueLabel={model.status.valueLabel}
        options={model.status.options}
        onChange={onStatus}
        icon={<Globe />}
        active={model.status.value !== null}
      />
      <FilterPill<string | null>
        label={model.folder.label}
        value={model.folder.value}
        valueLabel={model.folder.valueLabel}
        options={model.folder.options}
        onChange={onFolder}
        icon={<Folder />}
        active={model.folder.value !== null}
      />
      <FilterPill<string | null>
        label={model.model.label}
        value={model.model.value}
        valueLabel={model.model.valueLabel}
        options={model.model.options}
        onChange={onModel}
        icon={<Wand />}
        active={model.model.value !== null}
      />
      {/* Owner sits after Model and before Updated: it narrows by WHOSE, which
          is closer to the folder question than to the when one. */}
      <FilterPill<string | null>
        label={model.owner.label}
        value={model.owner.value}
        valueLabel={model.owner.valueLabel}
        options={model.owner.options}
        onChange={onOwner}
        icon={<Users />}
        active={model.owner.value !== null}
      />
      <FilterPill<UpdatedWindow>
        label={model.updated.label}
        value={model.updated.value}
        valueLabel={model.updated.valueLabel}
        options={model.updated.options}
        onChange={onUpdated}
        icon={<Clock />}
        active={model.updated.value !== null}
      />
      <span className="filter-bar__spacer" />
      {/* Sort never inverts: there is always a sort, so an "active" state would
          be on permanently and would say nothing. */}
      <FilterPill<DeckSort>
        label={model.sort.label}
        value={model.sort.value}
        valueLabel={model.sort.valueLabel}
        options={model.sort.options}
        onChange={onSort}
        icon={<ArrowUpDown />}
      />
      {view === 'grid' && <TileSizeControl value={tileSize} onChange={onTileSize} />}
      <ViewSwitcher value={view} onChange={onView} />
    </div>
  );
}
